import { useState, useEffect, useRef } from "react";
import {
  CallClient,
  CallAgent,
  Call,
  LocalAudioStream,
  RemoteAudioStream,
  DtmfTone,
} from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { API_ENDPOINTS, AZURE_CONFIG } from "../config/api.config";
import {
  CallStatusType,
  MicrophonePermission,
} from "../constants/callStatusConstants";

interface CallState {
  status: CallStatusType;
  duration: number;
  startTime?: Date;
  endTime?: Date;
  callId?: number;
  // Optional: store failure info for UI/debug
  endReason?: {
    code?: number;
    subCode?: number;
    message?: string;
  };
}

/** ------------ PHONE NORMALIZATION (AU-focused) -------------------
 * ACS PSTN requires E.164 for best reliability (especially landlines).
 * Examples:
 *  - 0732779888  -> +61732779888
 *  - 0412345678  -> +61412345678
 *  - +61412345678 stays the same
 *  - (07) 3277 9888 -> +61732779888
 */
const toE164_AU = (raw: string): string => {
  const cleaned = (raw || "").trim().replace(/[^\d+]/g, "");

  if (!cleaned) throw new Error("Phone number is empty");

  // Already E.164
  if (cleaned.startsWith("+")) {
    // Minimal sanity check
    if (!/^\+\d{8,15}$/.test(cleaned)) {
      throw new Error(`Invalid E.164 number: ${raw}`);
    }
    return cleaned;
  }

  // Remove any leading 00 international access prefix (e.g., 0061...)
  const n = cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
  if (n.startsWith("+")) return toE164_AU(n);

  // AU mobile: 04xxxxxxxx (10 digits) -> +614xxxxxxxx
  if (/^04\d{8}$/.test(n)) return `+61${n.substring(1)}`;

  // AU landline: 0[2378]xxxxxxxx (10 digits) -> +61[2378]xxxxxxxx
  if (/^0[2378]\d{8}$/.test(n)) return `+61${n.substring(1)}`;

  // If you want to accept 9-digit landlines missing the leading 0 (risky), uncomment:
  // if (/^[2378]\d{8}$/.test(n)) return `+61${n}`;

  throw new Error(
    `Phone number must be E.164 (or AU local like 04xxxxxxxx / 0[2378]xxxxxxxx). Got: ${raw}`
  );
};

/** ------------ VALIDATE ENV VARIABLE SAFELY ------------------- */
const getCallerId = (): string => {
  const callerId = process.env.REACT_APP_AZURE_CALLER_ID;
  if (!callerId) {
    throw new Error("REACT_APP_AZURE_CALLER_ID missing");
  }
  return callerId;
};
/** ------------------------------------------------------------- */

export const useCallManager = (phoneNumber: string) => {
  const [callState, setCallState] = useState<CallState>({
    status: CallStatusType.CONNECTING, // you may change to IDLE if you have that enum
    duration: 0,
  });

  const [callAgent, setCallAgent] = useState<CallAgent | null>(null);
  const [call, setCall] = useState<Call | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [microphonePermission, setMicrophonePermission] =
    useState<MicrophonePermission>(MicrophonePermission.PROMPT);

  // Streams for recording
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteAudioStreams, setRemoteAudioStreams] = useState<MediaStream[]>(
    []
  );

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDisposingRef = useRef<boolean>(false);
  const callClientRef = useRef<CallClient | null>(null);
  const localAudioStreamRef = useRef<LocalAudioStream | null>(null);

  /** ---------------- MICROPHONE PERMISSION ------------------ */
  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermission(MicrophonePermission.GRANTED);
      setLocalStream(stream);
      return stream;
    } catch (error) {
      setMicrophonePermission(MicrophonePermission.DENIED);
      // NOTE: don't cleanup here—caller will handle to avoid double-race
      throw error;
    }
  };

  /** ---------------- REMOTE AUDIO --------------------------- */
  const subscribeToRemoteAudio = async (stream: RemoteAudioStream) => {
    const mediaStream = await stream.getMediaStream();
    setRemoteAudioStreams((prev) => {
      if (prev.some((s) => s.id === mediaStream.id)) return prev;
      return [...prev, mediaStream];
    });
  };

  /** ---------------------- TIMER ---------------------------- */
  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCallState((prev) => ({ ...prev, duration: prev.duration + 1 }));
    }, 1000);
  };

  /** ------------------ CLEANUP -------------------------- */
  const cleanupExistingCall = async () => {
    if (isDisposingRef.current) return;
    isDisposingRef.current = true;

    try {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Stop local mic tracks (optional but recommended)
      if (localStream) {
        localStream.getTracks().forEach((t) => t.stop());
      }

      if (call && call.state !== "Disconnected") {
        try {
          await call.hangUp();
        } catch (e) {
          console.error("hangUp failed:", e);
        }
      }

      if (callAgent) {
        try {
          await callAgent.dispose();
        } catch (e) {
          console.error("callAgent.dispose failed:", e);
        }
      }

      callClientRef.current = null;
      localAudioStreamRef.current = null;

      setCall(null);
      setCallAgent(null);
      setIsMuted(false);
      setRemoteAudioStreams([]);
      setLocalStream(null);
    } finally {
      isDisposingRef.current = false;
    }
  };

  const handleRemoteHangup = async (endedCall?: Call) => {
    // capture end reason BEFORE cleanup clears call ref
    const endReason = endedCall?.callEndReason;
    if (endReason) {
      console.log("Call end reason:", endReason);
    }

    await cleanupExistingCall();

    setCallState((prev) => ({
      ...prev,
      status: CallStatusType.ENDED,
      endTime: new Date(),
      endReason: endReason
        ? {
          code: (endReason as any).code,
          subCode: (endReason as any).subCode,
          message: (endReason as any).message,
        }
        : prev.endReason,
    }));
  };

  /** --------------------- INITIATE CALL ---------------------- */
  const initiateCall = async (token: string) => {
    await cleanupExistingCall();

    setCallState({
      status: CallStatusType.CONNECTING,
      duration: 0,
      startTime: new Date(),
      callId: Date.now(),
      endReason: undefined,
    });

    await initializeAzureCall(token);
  };

  /** ------------------ AZURE CALL SETUP ---------------------- */
  const initializeAzureCall = async (token: string) => {
    await cleanupExistingCall();

    // 1) Normalize numbers to E.164 (fixes many landline failures)
    const destination = toE164_AU(phoneNumber);
    const callerId = toE164_AU(getCallerId());

    console.log("Dialing destination:", destination);
    console.log("Using callerId:", callerId);

    const callClient = new CallClient();
    callClientRef.current = callClient;

    const tokenCredential = new AzureCommunicationTokenCredential(token);
    const agent = await callClient.createCallAgent(tokenCredential, {
      displayName: AZURE_CONFIG.AGENT_DISPLAY_NAME,
    });

    setCallAgent(agent);

    let micStream: MediaStream;
    try {
      micStream = await checkMicrophonePermission();
    } catch (e) {
      console.error("Microphone permission denied:", e);
      setCallState((prev) => ({ ...prev, status: CallStatusType.FAILED }));
      await cleanupExistingCall();
      throw e;
    }

    const localAudioStream = new LocalAudioStream(micStream);
    localAudioStreamRef.current = localAudioStream;

    const newCall = agent.startCall([{ phoneNumber: destination }], {
      alternateCallerId: { phoneNumber: callerId },
      audioOptions: {
        localAudioStreams: [localAudioStream],
        muted: false,
      },
    });

    setCall(newCall);

    /** -------- CALL STATE -------- */
    newCall.on("stateChanged", () => {
      console.log("Call state changed:", newCall.state);

      if (newCall.state === "Connected") {
        setCallState((prev) => ({
          ...prev,
          status: CallStatusType.CONNECTED,
          startTime: new Date(),
        }));
        startTimer();
      }

      if (newCall.state === "Disconnected") {
        // IMPORTANT: inspect end reason to know why landline failed
        console.log("Call end reason:", newCall.callEndReason);
        handleRemoteHangup(newCall);
      }
    });

    /** -------- REMOTE AUDIO STREAMS -------- */
    newCall.on("remoteAudioStreamsUpdated", (e) => {
      e.added.forEach(async (stream: RemoteAudioStream) => {
        try {
          await subscribeToRemoteAudio(stream);
          console.log("Remote audio stream captured");
        } catch (err) {
          console.error("Failed to get remote audio stream", err);
        }
      });
    });
  };

  /** ------------------ TOGGLE MIC --------------------------- */
  const toggleMicrophone = async () => {
    if (!call) return;
    if (isMuted) {
      await call.unmute();
      setIsMuted(false);
    } else {
      await call.mute();
      setIsMuted(true);
    }
  };

  /** -------------------- END CALL ---------------------------- */
  const endCall = async () => {
    await cleanupExistingCall();
    setCallState((prev) => ({
      ...prev,
      status: CallStatusType.ENDED,
      endTime: new Date(),
    }));
  };

  /** ------------------ TOKEN FETCH -------------------------- */
  useEffect(() => {
    const getCallToken = async () => {
      try {
        const accessToken = localStorage.getItem("AccessToken");
        const response = await fetch(API_ENDPOINTS.CALLS.TOKEN, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId: "current-user" }),
        });

        const data = await response.json();
        await initiateCall(data.token);
      } catch (e) {
        console.error("Token fetch / call init failed:", e);
        setCallState((prev) => ({ ...prev, status: CallStatusType.FAILED }));
      }
    };

    getCallToken();
    // re-run if phoneNumber changes (optional):
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ------------------ UNMOUNT ------------------------------ */
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (call) call.hangUp().catch(console.error);
      cleanupExistingCall().catch(console.error);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ------------------ SEND DTMF TONES ---------------------- */
  const dtmfToneMap: Record<string, DtmfTone> = {
    "0": "Num0",
    "1": "Num1",
    "2": "Num2",
    "3": "Num3",
    "4": "Num4",
    "5": "Num5",
    "6": "Num6",
    "7": "Num7",
    "8": "Num8",
    "9": "Num9",
    "*": "Star",
    "#": "Pound",
  };
  const sendDtmfTone = async (tone: string) => {
    try {
      if (!call) return;

      const dtmfTone = dtmfToneMap[tone];

      if (!dtmfTone) {
        return;
      }

      await call.sendDtmf(dtmfTone);
    } catch (error) {
      console.error("Failed to send DTMF tone:", error);
    }
  };

  return {
    callState,
    isMuted,
    microphonePermission,
    toggleMicrophone,
    endCall,
    sendDtmfTone,

    // For recording
    localStream,
    remoteAudioStreams,
  };
};
